import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {ToastrService} from 'ngx-toastr';

interface ApolloLikeError {
  networkError?: unknown;
  graphQLErrors?: Array<{message?: string}>;
}

function isApolloLikeError(error: unknown): error is ApolloLikeError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('networkError' in error || 'graphQLErrors' in error)
  );
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  constructor(private toastr: ToastrService) {}

  handleMutationVerificationError(itemBeingMutated: string): void {
    this.showErrorDialog(
      $localize`An unusual error occurred while checking the customizations, please check if the item has been modified.`,
    );
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  handleDefault(error: any, location?: string): void {
    if (environment.production) {
      // Sentry.captureException(error.message);
    } else {
      console.error(error);
    }

    if (isApolloLikeError(error)) {
      let moreInfo = '';
      /* eslint-disable @typescript-eslint/no-explicit-any */
      if ((error.networkError as any)?.error?.errors) {
        const errorMessages = (error.networkError as any).error.errors.map(
          (e: {message: any}) => e.message,
        );
        if (errorMessages.length > 0) {
          moreInfo = $localize`Remaining information:` + ' ' + errorMessages.join(', ') + '.';
        }
      }

      if (error.networkError) {
        const networkError = error.networkError as Record<string, unknown>;
        if ('status' in networkError) {
          if (networkError['status'] === 0) {
            this.showErrorDialogWithTitle(
              $localize`No connection`,
              $localize`Check if you are connected to the internet.` + moreInfo,
            );
            return;
          }
        }

        if (error.networkError) {
          if ('statusCode' in networkError) {
            switch (networkError['statusCode']) {
              case 403:
                this.showErrorDialogWithTitle(
                  $localize`No access`,
                  $localize`You may not perform this action.` + moreInfo,
                );
                return;

              default:
                this.showErrorDialogWithTitle(
                  $localize`Server error`,
                  $localize`The server gave an error.` + moreInfo,
                );
                return;
            }
          }
        }
      }

      if (error.graphQLErrors?.[0]?.message) {
        this.showErrorDialog(error.graphQLErrors?.[0]?.message);
        return;
      }
    }

    this.showErrorDialog($localize`An unknown error has occurred.`);
  }

  showErrorDialog(message: string): void {
    this.toastr.error(message, $localize`Something went wrong`, {
      timeOut: 0,
    });
  }

  showErrorDialogWithTitle(title: string, message: string): void {
    this.toastr.error(message, title, {
      timeOut: 0,
    });
  }
}
